import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Grid, Card, CardContent, LinearProgress, Alert, Chip, Avatar, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { ArrowBack, Category, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </CardContent>
  </Card>
);
const typeColor: any = { RAW: 'info', SEMI: 'warning', FINISHED: 'success', CONSUMABLE: 'default' };

const Material360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { financeWorkspaceService.material360(Number(id)).then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false)); }, [id]);
  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (error || !d) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>;
  const p = d.profile; const st = d.stock || {};
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: 'info.main' }}><Category /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{p.name}</Typography>
            {p.materialType && <Chip size="small" label={p.materialType} color={typeColor[p.materialType] || 'default'} />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <span style={{ fontFamily: 'monospace' }}>{p.code}</span> · HSN {p.hsn || '—'} · GST {p.gstRate}% · {p.category || ''}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="In Stock" value={`${st.qty ?? 0}`} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Stock Value" value={formatIndianNumber(st.value || 0)} color="primary.main" /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Total Received" value={`${st.received ?? 0}`} color="success.main" /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Total Issued" value={`${st.issued ?? 0}`} color="warning.main" /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Stock by Warehouse</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Warehouse</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Avg Cost</TableCell></TableRow></TableHead>
              <TableBody>
                {d.warehouses.map((w: any, i: number) => (
                  <TableRow key={i} hover><TableCell>{w.warehouse}</TableCell><TableCell align="right">{w.qty}</TableCell><TableCell align="right">{formatIndianNumber(w.cost)}</TableCell></TableRow>
                ))}
                {!d.warehouses.length && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No stock.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Stock Movement</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell align="right">Qty</TableCell><TableCell>Reference</TableCell></TableRow></TableHead>
              <TableBody>
                {d.movements.map((m: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell>{String(m.ts).slice(0, 10)}</TableCell>
                    <TableCell>{m.type === 'IN' ? <Chip size="small" icon={<ArrowDownward />} label="IN" color="success" /> : m.type === 'OUT' ? <Chip size="small" icon={<ArrowUpward />} label="OUT" color="warning" /> : <Chip size="small" label={m.type} />}</TableCell>
                    <TableCell align="right">{m.qty}</TableCell>
                    <TableCell>{(m.ref || '').replace('_', ' ')}{m.remarks ? ` · ${m.remarks}` : ''}</TableCell>
                  </TableRow>
                ))}
                {!d.movements.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No movement.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Material360Page;
